import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { QuickBookButtons } from "@nodus/booking";
import { X, Users, Camera, Tv, Volume2, Clock, Navigation, Calendar, Search, MapPin, ArrowRight, DoorOpen } from "lucide-react";
import { ActionIcon, Badge, Button, Text, TextInput, Divider, Stack, Group, Box, UnstyledButton } from "@mantine/core";
import type { NavigateResponse } from "@/lib/api/client";
import { formatRouteSummary } from "@/lib/route-summary";

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatDuration(minutes: number) {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

interface RoomOption {
  id: number;
  name: string;
  description: string | null;
  floor_name: string;
  address_name: string;
}

interface FloorplanRoomPanelProps {
  room: {
    id: number;
    name: string;
    description: string | null;
    floor_name: string;
    address_name: string;
    room_type: string | null;
    is_available: boolean | null;
    is_bookable: boolean;
    seats: number | null;
    has_camera: boolean;
    has_tv: boolean;
    has_speaker: boolean;
    current_event: { title: string; start: string; end: string; organizer_tenant_name: string | null } | null;
    next_free_slot_start: string | null;
    next_free_slot_duration_minutes: number | null;
    booking_slots: any[] | null;
    navigation_hints: string;
  } | null;
  opened: boolean;
  onClose: () => void;
  onBook?: (roomId: number, start: string, end: string) => void;
  onNavigate?: (roomId: number, fromRoomId?: number) => void;
  navigationResult?: NavigateResponse | null;
  navigationError?: string | null;
  onClearNavigation?: () => void;
  allRooms?: RoomOption[];
  navFromRoomId?: number | null;
  onNavFromRoomChange?: (roomId: number) => void;
  isNavigating?: boolean;
  /** Route from this room to the nearest exit (#473). */
  onNearestExit?: (fromRoomId: number) => void;
  isFindingExit?: boolean;
}

function RoomPicker({ rooms, selectedId, onChange }: { rooms: RoomOption[]; selectedId: number | null; onChange: (id: number) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = rooms.find(r => r.id === selectedId);
  const q = search.toLowerCase();

  const grouped = useMemo(() => {
    const map = new Map<string, RoomOption[]>();
    for (const r of rooms) {
      const key = `${r.address_name} — ${r.floor_name}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rooms]);

  return (
    <Box pos="relative">
      <Text fz="xs" c="dimmed" mb={4}>Navigate from</Text>
      <Button
        variant="default"
        size="compact-sm"
        fullWidth
        onClick={() => setOpen(!open)}
        styles={{ label: { justifyContent: "flex-start", fontWeight: 400 } }}
      >
        {selected ? (selected.description || selected.name) : "Select room..."}
      </Button>
      {open && (
        <Box
          pos="absolute"
          left={0}
          right={0}
          style={{
            top: "100%", marginTop: 4, zIndex: 50,
            borderRadius: 6, border: "1px solid var(--mantine-color-default-border)",
            background: "var(--mantine-color-body)",
            boxShadow: "var(--mantine-shadow-lg)",
            maxHeight: 192, display: "flex", flexDirection: "column",
          }}
        >
          <Box p={4}>
            <TextInput
              size="xs"
              placeholder="Search rooms..."
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<Search size={12} />}
              autoFocus
            />
          </Box>
          <Box style={{ overflowY: "auto", padding: "4px 0" }}>
            {grouped.map(([group, roomList]) => {
              const visible = roomList.filter(r =>
                !q || (r.description || r.name).toLowerCase().includes(q) || r.name.toLowerCase().includes(q),
              );
              if (!visible.length) return null;
              return (
                <div key={group}>
                  <Text fz={10} fw={600} c="dimmed" tt="uppercase" px="xs" py={4} pos="sticky" top={0} bg="var(--mantine-color-body)" style={{ zIndex: 10 }}>
                    {group}
                  </Text>
                  {visible.map(r => (
                    <UnstyledButton
                      key={r.id}
                      style={{
                        width: "100%", textAlign: "left", padding: "4px 8px", fontSize: 13,
                        borderRadius: 2, display: "flex", alignItems: "center", gap: 8,
                        background: r.id === selectedId ? "var(--mantine-color-default-hover)" : "transparent",
                      }}
                      onClick={() => { onChange(r.id); setOpen(false); setSearch(""); }}
                    >
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.description || r.name}</span>
                      {r.description && <Text fz="xs" c="dimmed" style={{ flexShrink: 0 }}>{r.name}</Text>}
                    </UnstyledButton>
                  ))}
                </div>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function FloorplanRoomPanel({ room, opened, onClose, onBook, onNavigate, navigationResult, navigationError, onClearNavigation, allRooms, navFromRoomId, onNavFromRoomChange, isNavigating, onNearestExit, isFindingExit }: FloorplanRoomPanelProps) {
  const displayName = room?.description || room?.name;

  return (
    <Box
      pos="fixed"
      style={{
        zIndex: 50, boxShadow: "var(--mantine-shadow-lg)",
        transition: "transform 200ms ease-in-out, height 200ms ease-in-out",
        backdropFilter: "blur(12px)",
        backgroundColor: "rgba(15, 23, 42, 0.85)",
        // Portrait: bottom sheet — expand when showing navigation
        bottom: 0, left: 0, right: 0,
        height: navigationResult ? "80vh" : "35vh",
        width: "100%",
        borderTop: "1px solid var(--mantine-color-default-border)",
        transform: opened && room ? "translateY(0)" : "translateY(100%)",
      }}
      // Override for landscape
      className="floorplan-panel"
    >
      <style>{`
        @media (orientation: landscape) {
          .floorplan-panel {
            top: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            left: auto !important;
            height: 100% !important;
            width: 300px !important;
            border-top: none !important;
            border-left: 1px solid var(--mantine-color-default-border) !important;
            transform: ${opened && room ? "translateX(0)" : "translateX(100%)"} !important;
          }
        }
      `}</style>
      {room && (
        <Box h="100%" p={20} style={{ overflowY: "auto" }}>
          {/* Close button */}
          <ActionIcon
            variant="subtle"
            onClick={onClose}
            pos="absolute" top={12} right={12}
            aria-label="Close panel"
          >
            <X size={20} />
          </ActionIcon>

          {/* Header */}
          <Box pr={32} mb="md">
            <Text fz="lg" fw={600} lh={1.3}>{displayName}</Text>
            <Text fz="xs" c="dimmed" mt={2}>
              {room.description ? room.name : ""}{room.description ? " · " : ""}
              {room.floor_name}, {room.address_name}
            </Text>
          </Box>

          {/* Availability badge */}
          <Box mb="md">
            {!room.is_bookable ? (
              <Badge variant="light" color="gray">Not bookable</Badge>
            ) : room.is_available ? (
              <Badge color="green">Available</Badge>
            ) : (
              <Badge color="red">Booked</Badge>
            )}
          </Box>

          {/* Current event */}
          {!room.is_available && room.current_event && (
            <Box mb="md" p="sm" style={{ borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.05)" }}>
              <Text fz={10} tt="uppercase" c="dimmed" mb={4}>Current booking</Text>
              <Text fz="sm" fw={500} truncate="end">{room.current_event.title || "Booked"}</Text>
              <Text fz="xs" c="dimmed">
                {formatTime(room.current_event.start)} – {formatTime(room.current_event.end)}
              </Text>
              {room.current_event.organizer_tenant_name && (
                <Text fz="xs" c="dimmed">{room.current_event.organizer_tenant_name}</Text>
              )}
            </Box>
          )}

          {/* Free slot info */}
          {room.next_free_slot_start && room.next_free_slot_duration_minutes != null && (
            <Group gap={6} mb="md">
              <Clock size={14} style={{ flexShrink: 0, color: "var(--mantine-color-dimmed)" }} />
              <Text fz="sm" c="dimmed">
                {room.is_available
                  ? `Free for ${formatDuration(room.next_free_slot_duration_minutes)}`
                  : `Free in ${formatDuration(Math.max(0, Math.round((new Date(room.next_free_slot_start).getTime() - Date.now()) / 60000)))}`}
              </Text>
            </Group>
          )}

          {/* Quick-book buttons */}
          {room.is_available && room.is_bookable && room.next_free_slot_start && room.next_free_slot_duration_minutes != null && onBook && (
            <Box mb="md">
              <Text fz={10} tt="uppercase" c="dimmed" mb="xs">Quick book</Text>
              <Group gap="xs">
                <QuickBookButtons
                  roomId={room.id}
                  freeSlotStart={room.next_free_slot_start!}
                  freeSlotDurationMinutes={room.next_free_slot_duration_minutes!}
                  onBook={(_id, start, end) => onBook!(room.id, start, end)}
                />
              </Group>
            </Box>
          )}

          {/* Amenities */}
          {(room.seats != null || room.has_camera || room.has_tv || room.has_speaker) && (
            <Box mb="md">
              <Text fz={10} tt="uppercase" c="dimmed" mb="xs">Amenities</Text>
              <Group gap="xs" wrap="wrap">
                {room.seats != null && (
                  <Badge variant="outline" size="sm" leftSection={<Users size={12} />}>{room.seats} seats</Badge>
                )}
                {room.has_camera && (
                  <Badge variant="outline" size="sm" leftSection={<Camera size={12} />}>Camera</Badge>
                )}
                {room.has_tv && (
                  <Badge variant="outline" size="sm" leftSection={<Tv size={12} />}>Display</Badge>
                )}
                {room.has_speaker && (
                  <Badge variant="outline" size="sm" leftSection={<Volume2 size={12} />}>Speaker</Badge>
                )}
              </Group>
            </Box>
          )}

          {/* Navigation hints */}
          {room.navigation_hints && (
            <Box mb="md">
              <Text fz={10} tt="uppercase" c="dimmed" mb={4}>How to find</Text>
              <Text fz="sm" fs="italic" c="dimmed">{room.navigation_hints}</Text>
            </Box>
          )}

          {/* Actions */}
          <Stack gap="xs" mt="lg">
            {onNavigate && (
              <Stack gap="xs">
                {allRooms && allRooms.length > 0 && onNavFromRoomChange && (
                  <RoomPicker
                    rooms={allRooms.filter(r => r.id !== room.id)}
                    selectedId={navFromRoomId ?? null}
                    onChange={onNavFromRoomChange}
                  />
                )}
                <Button
                  variant="outline"
                  color="blue"
                  fullWidth
                  onClick={() => onNavigate(room.id, navFromRoomId ?? undefined)}
                  disabled={isNavigating || !navFromRoomId}
                  leftSection={<Navigation size={16} />}
                >
                  {isNavigating ? "Computing route..." : "Navigate here"}
                </Button>
                {navigationError && (
                  <Box p="xs" style={{ borderRadius: 6, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)" }}>
                    <Text fz="xs" c="red">{navigationError}</Text>
                  </Box>
                )}
              </Stack>
            )}

            {onNearestExit && (
              <Button
                variant="outline"
                color="green"
                fullWidth
                onClick={() => onNearestExit(room.id)}
                disabled={isFindingExit}
                leftSection={<DoorOpen size={16} />}
              >
                {isFindingExit ? "Finding the nearest exit..." : "Nearest exit from here"}
              </Button>
            )}

            {room.is_bookable && room.is_available !== null && (
              <Button variant="outline" fullWidth component={Link} to={`/meeting-rooms/${room.id}`} leftSection={<Calendar size={16} />}>
                View schedule
              </Button>
            )}
          </Stack>

          {/* Navigation directions — inline when result exists */}
          {navigationResult && (
            <Box mt="md">
              <Divider mb="sm" />
              <Group justify="space-between" mb="xs">
                <Text fz={10} tt="uppercase" c="dimmed" fw={500}>
                  Directions · {formatRouteSummary(navigationResult, { floorChanges: false })}
                </Text>
                {onClearNavigation && (
                  <Button variant="subtle" size="compact-xs" c="dimmed" onClick={onClearNavigation}>
                    Clear
                  </Button>
                )}
              </Group>
              <Stack gap={4}>
                {navigationResult.instructions.map((inst, i) => (
                  <Group key={i} gap="xs" align="flex-start" py={4}>
                    <Box w={20} ta="center" style={{ flexShrink: 0 }}>
                      {inst.type === "depart" ? <MapPin size={14} style={{ color: "var(--mantine-color-green-5)" }} /> :
                       inst.type === "arrive" ? <MapPin size={14} style={{ color: "var(--mantine-color-red-5)" }} /> :
                       inst.type === "transition" ? <ArrowRight size={14} style={{ color: "var(--mantine-color-blue-5)" }} /> :
                       <Text fz={11} c="dimmed" ta="center">{i}</Text>}
                    </Box>
                    <Text fz="sm" fw={inst.type === "depart" || inst.type === "arrive" ? 500 : 400} flex={1}>
                      {inst.text}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
